import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import { plumbingRoomKeys } from '../../core/query-keys';
import { plumbingRoomEndpoints } from './plumbing-rooms.endpoints';
import type {
  PlumbingRoom,
  CreatePlumbingRoomRequest,
  UpdatePlumbingRoomRequest,
} from './plumbing-rooms.types';

export function usePlumbingRooms(codeContains = '') {
  return useQuery({
    queryKey: [...plumbingRoomKeys.list, codeContains],
    queryFn: async () =>
      (
        await api.get<{ plumbing_rooms: PlumbingRoom[]; next_page_token: string }>(
          withQuery(plumbingRoomEndpoints.list, { plumbing_room_code_contains: codeContains }),
        )
      ).plumbing_rooms ?? [],
  });
}

export function usePlumbingRoom(id: string) {
  return useQuery({
    queryKey: plumbingRoomKeys.one(id),
    queryFn: () => api.get<PlumbingRoom>(plumbingRoomEndpoints.one(id)),
    enabled: Boolean(id),
  });
}

export function useCreatePlumbingRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreatePlumbingRoomRequest) => api.post<PlumbingRoom>(plumbingRoomEndpoints.list, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomKeys.all }),
  });
}

export function useUpdatePlumbingRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: UpdatePlumbingRoomRequest & { id: string }) =>
      api.patch<PlumbingRoom>(plumbingRoomEndpoints.one(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomKeys.all }),
  });
}

export function useDeletePlumbingRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(plumbingRoomEndpoints.one(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomKeys.all }),
  });
}
